import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Plus, Share2, CalendarDays, ListChecks, Sparkles, UtensilsCrossed, ShoppingCart, Trophy, LayoutGrid, Rows3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMembers } from "@/context/MemberContext";
import { MemberAvatar } from "@/components/MemberAvatar";
import { MemberDialog } from "@/components/home/MemberDialog";
import { COLOR_OPTIONS, accentHex } from "@/lib/constants";
import { cn } from "@/lib/utils";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1713942589575-abe9bbe7095e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBraXRjaGVuJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODY0ODgwMzl8MA&ixlib=rb-4.1.0&q=85";

const QUICK = [
  { to: "/chores", label: "Chores", icon: ListChecks, color: "#FF6F61" },
  { to: "/cleaning", label: "Cleaning", icon: Sparkles, color: "#06D6A0" },
  { to: "/meals", label: "Meals", icon: UtensilsCrossed, color: "#FFD166" },
  { to: "/shopping", label: "Shopping", icon: ShoppingCart, color: "#CDB4DB" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, color: "#4FC3F7" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Home() {
  const { user, updatePreferences } = useAuth();
  const [layout, setLayout] = useState(user?.home_layout || "vertical");
  const changeLayout = (mode) => {
    setLayout(mode);
    updatePreferences(mode).catch(() => {});
  };
  const { members, refreshMembers } = useMembers();
  const [summary, setSummary] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [memberDialog, setMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [mForm, setMForm] = useState({ name: "", role: "kid", color: "coral" });

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([api("/reports/summary"), api("/reports/weekly-digest")]);
      setSummary(s);
      setDigest(d);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAddMember = () => {
    setEditingMember(null);
    setMForm({ name: "", role: "kid", color: COLOR_OPTIONS[members.length % COLOR_OPTIONS.length] });
    setMemberDialog(true);
  };
  const openEditMember = (m) => {
    setEditingMember(m);
    setMForm({ name: m.name, role: m.role, color: m.color });
    setMemberDialog(true);
  };

  const saveMember = async () => {
    if (!mForm.name.trim()) return toast.error("Add a name");
    const body = { name: mForm.name.trim(), role: mForm.role, color: mForm.color };
    try {
      if (editingMember) await api(`/members/${editingMember.id}`, { method: "PUT", body });
      else await api("/members", { method: "POST", body });
      setMemberDialog(false);
      await refreshMembers();
    } catch {
      toast.error("Could not save member");
    }
  };

  const deleteMember = async () => {
    if (!editingMember) return;
    try {
      await api(`/members/${editingMember.id}`, { method: "DELETE" });
      setMemberDialog(false);
      await refreshMembers();
    } catch {
      toast.error("Could not delete");
    }
  };

  const shareDigest = async () => {
    if (!digest) return;
    const lines = [`BentoLife — week ahead`, `${digest.event_count} events coming up.`];
    (digest.upcoming_events || []).slice(0, 5).forEach((e) => {
      const when = e.all_day ? "All day" : format(parseISO(e.start_date), "EEE h:mm a");
      lines.push(`• ${when} — ${e.title}${e.member_name ? ` (${e.member_name})` : ""}`);
    });
    const text = lines.join("\n");
    try {
      if (navigator.share) await navigator.share({ title: "BentoLife weekly digest", text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Digest copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  const upcoming = digest?.upcoming_events || [];

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(44,42,41,0.82) 0%, rgba(44,42,41,0.45) 55%, rgba(44,42,41,0.15) 100%)" }} />
        <div className="relative flex flex-col gap-3 p-6 sm:p-8">
          <div>
            <p className="text-sm font-semibold text-white/80">{format(new Date(), "EEEE, MMMM d")}</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-white sm:text-4xl">
              {greeting()}, {user?.name?.split(" ")[0] || "there"}!
            </h1>
          </div>
          <Button
            onClick={shareDigest}
            className="w-fit rounded-full bg-white/90 text-ink hover:bg-white"
            data-testid="share-digest-btn"
          >
            <Share2 className="mr-1.5 h-4 w-4" /> Share weekly digest
          </Button>
        </div>
      </div>

      {/* Member switcher */}
      <div className="bento-tile p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Family</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-1">
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => openEditMember(m)}
              className="flex flex-col items-center gap-1.5 bento-pressable"
              data-testid={`home-member-${m.id}`}
            >
              <MemberAvatar member={m} size="lg" />
              <span className="max-w-[64px] truncate text-xs font-semibold text-ink">{m.name}</span>
              <span className="text-[10px] font-semibold text-clay">{m.points} pts</span>
            </button>
          ))}
          <button
            onClick={openAddMember}
            className="flex flex-col items-center gap-1.5"
            data-testid="home-add-member-btn"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-dashed border-border text-muted-foreground">
              <Plus className="h-6 w-6" />
            </span>
            <span className="text-xs font-semibold text-muted-foreground">Add</span>
          </button>
        </div>
      </div>

      {/* Layout toggle */}
      <div className="ml-auto flex w-fit items-center gap-1 rounded-full bg-secondary p-1" data-testid="layout-toggle">
        <button
          onClick={() => changeLayout("vertical")}
          data-testid="layout-vertical-btn"
          className={cn(
            "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
            layout === "vertical" ? "bg-card text-ink shadow-sm" : "text-muted-foreground"
          )}
        >
          <Rows3 className="h-4 w-4" /> Vertical
        </button>
        <button
          onClick={() => changeLayout("blocks")}
          data-testid="layout-blocks-btn"
          className={cn(
            "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
            layout === "blocks" ? "bg-card text-ink shadow-sm" : "text-muted-foreground"
          )}
        >
          <LayoutGrid className="h-4 w-4" /> Blocks
        </button>
      </div>

      {layout === "blocks" ? (
        <BlocksDashboard members={members} summary={summary} digest={digest} loading={loading} upcoming={upcoming} />
      ) : (
      <>
      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {QUICK.map((q) => (
          <Link
            key={q.to}
            to={q.to}
            data-testid={`quick-${q.label.toLowerCase()}`}
            className="bento-tile bento-pressable flex flex-col items-start gap-3 p-5"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary text-ink">
              <q.icon className="h-5 w-5" />
            </span>
            <span className="font-display font-semibold text-ink">{q.label}</span>
          </Link>
        ))}
      </div>

      {/* Stats + upcoming */}
      {loading ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <Skeleton className="h-48 rounded-3xl" />
          <Skeleton className="h-48 rounded-3xl" />
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {/* Next up */}
          <div className="bento-tile p-5">
            <div className="mb-3 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-sky" />
              <h2 className="font-display text-lg font-semibold text-ink">Next up</h2>
            </div>
            {upcoming.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No upcoming events this week.</p>
            ) : (
              <div className="space-y-2">
                {upcoming.slice(0, 5).map((e) => (
                  <div key={e.id} className="flex items-center gap-3" data-testid={`home-event-${e.id}`}>
                    <div
                      className="h-9 w-1.5 rounded-full"
                      style={{ backgroundColor: accentHex(e.member_color) }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{e.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.all_day ? "All day" : format(parseISO(e.start_date), "EEE, h:mm a")}
                        {e.member_name ? ` · ${e.member_name}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chore progress */}
          <div className="bento-tile p-5">
            <div className="mb-3 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-clay" />
              <h2 className="font-display text-lg font-semibold text-ink">Points & chores</h2>
            </div>
            <div className="mb-4 grid grid-cols-3 gap-3 text-center">
              <Stat label="Points" value={summary?.total_points ?? 0} />
              <Stat label="Chores" value={summary?.chores ?? 0} />
              <Stat label="To buy" value={summary?.shopping_open ?? 0} />
            </div>
            <div className="space-y-2">
              {(digest?.members || []).slice(0, 4).map((m) => (
                <div key={m.member_id} className="flex items-center gap-3">
                  <MemberAvatar member={{ name: m.name, color: m.color }} size="sm" />
                  <span className="flex-1 truncate text-sm font-semibold text-ink">{m.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {m.pending_count} pending · {m.points} pts
                  </span>
                </div>
              ))}
              {(!digest?.members || digest.members.length === 0) && (
                <p className="py-2 text-center text-sm text-muted-foreground">Add family members to track points.</p>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* Member dialog */}
      <MemberDialog
        open={memberDialog}
        onOpenChange={setMemberDialog}
        editing={editingMember}
        form={mForm}
        setForm={setMForm}
        onSave={saveMember}
        onDelete={deleteMember}
      />
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="bento-tile bento-pressable flex flex-col gap-2 p-5" data-testid={`block-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary text-ink">
        <Icon className="h-5 w-5" />
      </span>
      <p className="font-display text-3xl font-bold text-ink">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </Link>
  );
}

function BlocksDashboard({ members, summary, digest, loading, upcoming }) {
  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {["a", "b", "c"].map((k) => (
          <Skeleton key={k} className="h-40 rounded-3xl" />
        ))}
      </div>
    );
  }
  const dmembers = digest?.members || [];
  const pendingTotal = dmembers.reduce((s, m) => s + (m.pending_count || 0), 0);
  const findD = (id) => dmembers.find((x) => x.member_id === id);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="blocks-dashboard">
      <StatBlock icon={ListChecks} label="Chores due" value={pendingTotal} to="/chores" />
      <StatBlock icon={Trophy} label="Total points" value={summary?.total_points ?? 0} to="/rewards" />
      <StatBlock icon={ShoppingCart} label="To buy" value={summary?.shopping_open ?? 0} to="/shopping" />

      {members.map((m) => {
        const d = findD(m.id);
        return (
          <div key={m.id} className="bento-tile flex flex-col gap-2 p-5" data-testid={`block-member-${m.id}`}>
            <div className="flex items-center gap-2">
              <MemberAvatar member={m} size="md" />
              <div className="min-w-0">
                <p className="truncate font-display font-semibold text-ink">{m.name}</p>
                <p className="text-xs font-semibold text-clay">{m.points} pts</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{d?.pending_count ?? 0} chores to do</p>
          </div>
        );
      })}

      <div className="bento-tile p-5 sm:col-span-2 lg:col-span-3">
        <div className="mb-2 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-sky" />
          <h2 className="font-display text-lg font-semibold text-ink">Next up</h2>
        </div>
        {!upcoming || upcoming.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No upcoming events this week.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {upcoming.slice(0, 6).map((e) => (
              <div key={e.id} className="flex items-center gap-3" data-testid={`block-event-${e.id}`}>
                <div className="h-9 w-1.5 rounded-full" style={{ backgroundColor: accentHex(e.member_color) }} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{e.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {e.all_day ? "All day" : format(parseISO(e.start_date), "EEE, h:mm a")}
                    {e.member_name ? ` · ${e.member_name}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-secondary/50 py-3">
      <p className="font-display text-2xl font-bold text-ink">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
